import {Title} from 'src/design/atoms/Title';
import {useDisclosure} from '@mantine/hooks';
import fileDownload from 'js-file-download';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {
  ItemAssignmentSimpleResponse,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {useDeleteItemAssignmentMutation} from 'src/redux/queries/itemAssignment';
import {useExportItemStatusReportMutation} from 'src/redux/queries/itemDefinition';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {Pill} from 'src/design/atoms/Pill';
import {
  UserStatePills,
  UserTypePills,
} from 'src/shared/constants/PillConstants';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {
  ItemAssignmentStateDataOptions,
  UserStateDataOptions,
  UserTypeDataOptions,
} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useGetItemAssignmentListV2Query} from 'src/redux/queries/itemAssignment';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {ItemAssignmentPillConfig} from './itemAssignmentHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface ItemAssignmentsTableProps {
  itemDefinitionId: string;
  itemDefinitionLabel: string;
}

export const ItemAssignmentsTable: React.FC<ItemAssignmentsTableProps> = ({
  itemDefinitionId,
  itemDefinitionLabel,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const {hasPermission: hasCreatePermission} = useHasModulePermission({
    module: ModuleEnum.ItemTrackerAssignments,
    operation: PermissionOperationEnum.Create,
  });

  const {hasPermission: hasDeletePermission} = useHasModulePermission({
    module: ModuleEnum.ItemTrackerAssignments,
    operation: PermissionOperationEnum.Delete,
  });

  const {hasPermission: hasExportPermission} = useHasModulePermission({
    module: ModuleEnum.ItemTrackerAssignments,
    operation: PermissionOperationEnum.Export,
  });

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  const [triggerExportItemStatusReport] = useExportItemStatusReportMutation();
  const [triggerDeleteItemAssignment] = useDeleteItemAssignmentMutation();

  const columns = useMemo<MRT_ColumnDef<ItemAssignmentSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'assignedToUser.name',
        header: 'Assigned To',
      },
      {
        accessorKey: 'assignedByUser.name',
        header: 'Assigned By',
      },
      {
        accessorKey: 'internalIdentifier',
        header: 'Identifier',
      },
      {
        accessorKey: 'state',
        header: 'Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: ItemAssignmentStateDataOptions,
        },
        Cell: ({cell}) => (
          <>
            {ItemAssignmentPillConfig(cell.row.original).map(pill => (
              <Pill {...pill} />
            ))}
          </>
        ),
      },
      {
        accessorKey: 'serial',
        header: 'Serial',
        maxSize: 150,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        Cell: ({cell}) => (
          <>{cell.getValue<string | undefined>()?.abbreviate(50)}</>
        ),
      },
      {
        id: 'createdAt',
        accessorFn: row => (row.createdAt ? new Date(row.createdAt) : null),
        filterVariant: 'date',
        header: 'Created On',
        Cell: ({cell}) => (
          <>{cell.getValue<Date | undefined>()?.toString().toShortDate()}</>
        ),
      },
      {
        id: 'assignedAt',
        accessorFn: row => (row.assignedAt ? new Date(row.assignedAt) : null),
        filterVariant: 'date',
        header: 'Assigned On',
        Cell: ({cell}) => (
          <>{cell.getValue<Date | undefined>()?.toString().toShortDate()}</>
        ),
      },
      {
        id: 'expiresAt',
        accessorFn: row => (row.expiresAt ? new Date(row.expiresAt) : null),
        filterVariant: 'date',
        header: 'Expires On',
        Cell: ({cell}) => (
          <>{cell.getValue<Date | undefined>()?.toString().toShortDate()}</>
        ),
      },
      {
        id: 'closedAt',
        accessorFn: row => (row.closedAt ? new Date(row.closedAt) : null),
        filterVariant: 'date',
        header: 'Closed On',
        Cell: ({cell}) => (
          <>{cell.getValue<Date | undefined>()?.toString().toShortDate()}</>
        ),
      },
      {
        accessorKey: 'assignedToUser.userType',
        header: 'User Type',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: UserTypeDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...UserTypePills(cell.getValue<UserTypeEnum>())} />
        ),
      },
      {
        accessorKey: 'assignedToUser.active',
        header: 'User Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: UserStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...UserStatePills(cell.getValue<UserStatusEnum>())} />
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'item-assignment-v2',
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'createdAt', desc: true}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isFetching, isError, refetch} =
    useGetItemAssignmentListV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
        itemDefinitionId: Number(itemDefinitionId),
      },
      {skip: !itemDefinitionId}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleAddNew = () =>
    navigate(PathUtility.ItemAssignmentCreate(itemDefinitionId));

  const handleNavigateToAssignment = (row: ItemAssignmentSimpleResponse) =>
    navigate(PathUtility.ItemAssignmentView(row.itemDefinitionId, row.id));

  const handleNavigateToCertificate = (row: ItemAssignmentSimpleResponse) =>
    navigate(
      PathUtility.ItemTrackerIssueCertificate(row.itemDefinitionId, row.id)
    );

  const handleNavigateToClone = (row: ItemAssignmentSimpleResponse) =>
    navigate(PathUtility.ItemAssignmentClone(row.itemDefinitionId, row.id));

  const exportAssignment = async () => {
    try {
      const response = await triggerExportItemStatusReport({
        itemDefinitionId: +itemDefinitionId,
      }).unwrap();

      fileDownload(
        response!.data,
        `item_for_${itemDefinitionLabel}_export.csv`
      );
      setAlert('Your file has been successfully exported. ', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleDeleteClick = (row: ItemAssignmentSimpleResponse) => {
    setDeleteTargetId(row.id!);
    confirmDeleteHandlers.open();
  };

  const handleDeleteConfirm = async () => {
    confirmDeleteHandlers.close();
    if (deleteTargetId === null) return;

    try {
      await triggerDeleteItemAssignment(deleteTargetId).unwrap();
      setAlert('Item assignment was successfully deleted.', 'success');
      void refetch();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setDeleteTargetId(null);
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => handleNavigateToAssignment(row.original)}
          />
          <TableRowActions.Menu>
            {row.original.hasIssueCertificate && (
              <TableRowActions.MenuItem
                onClick={() => handleNavigateToCertificate(row.original)}>
                View Certificate
              </TableRowActions.MenuItem>
            )}
            {hasCreatePermission && (
              <TableRowActions.MenuItem
                onClick={() => handleNavigateToClone(row.original)}>
                Clone
              </TableRowActions.MenuItem>
            )}
            {hasDeletePermission && (
              <TableRowActions.MenuItem
                color="red"
                onClick={() => handleDeleteClick(row.original)}>
                Delete
              </TableRowActions.MenuItem>
            )}
          </TableRowActions.Menu>
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {hasCreatePermission && (
              <TableToolbar.Action variant="create" onClick={handleAddNew}>
                New Assignment
              </TableToolbar.Action>
            )}
            {hasExportPermission && (
              <TableToolbar.Action
                variant="export"
                onClick={exportAssignment}
              />
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper>
      <Title pb="lg">Item Assignments</Title>
      {table}
      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDeleteConfirm}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this item assignment?"
        body="This action cannot be undone. All data relating to this record will also be deleted, including notes and notification history."
      />
    </ContentWrapper>
  );
};
